/* eslint-disable max-lines */
import { DO_CLICK_ACTIONS, DO_IMPRESSION_ACTIONS, SCREEN_NAME } from "../constants";
import { AnalyticsContextValue } from "@swiggy-private/checkout-core";

const SPLIT_BILL_CONTEXT_KEYS = {
    SELECTED_UPI_HANDLE: "selected upi handle",
    SUGGESTED_UPI_HANDLES: "suggested upi handles",
};

const SPLIT_BILL_LABELS = {
    UPI_HANDLE: "upi-handle",
};

interface SplitBillWidgetImpressionParams {
    orderId: string;
    totalBillAmount: number;
    amountPaidByUser: number;
    restaurantId: string;
}

export const impressionSplitBillWidget = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { orderId, totalBillAmount, amountPaidByUser, restaurantId }: SplitBillWidgetImpressionParams,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT_SUCCESS,
        action: DO_IMPRESSION_ACTIONS.SPLIT_BILL_WIDGET,
        label: restaurantId,
        context: JSON.stringify({
            orderId,
            totalBillAmount,
            amountPaidByUser,
        }),
    });
};

export const clickSplitBillWidget = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { orderId, totalBillAmount, amountPaidByUser, restaurantId }: SplitBillWidgetImpressionParams,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT_SUCCESS,
        action: DO_CLICK_ACTIONS.SPLIT_BILL_WIDGET,
        label: restaurantId,
        context: JSON.stringify({
            orderId,
            totalBillAmount,
            amountPaidByUser,
        }),
    });
};

interface SplitMethodTabImpressionParams {
    orderId: string;
    amountPaidByUser: number;
    numberOfGuest: number;
    restaurantId: string;
    tabType: string;
    position: number;
}

export const impressionSplitMethodTab = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { orderId, amountPaidByUser, numberOfGuest, restaurantId, tabType, position }: SplitMethodTabImpressionParams,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT_SUCCESS,
        action: DO_IMPRESSION_ACTIONS.SPLIT_METHOD_TAB, // Using single action for all tabs
        label: tabType,
        value: String(position),
        context: JSON.stringify({
            orderId,
            amountPaidByUser,
            numberOfGuest,
            restaurantId,
        }),
    });
};

interface SplitMethodTabClickParams {
    orderId: string;
    amountPaidByUser: number;
    numberOfGuest: number;
    restaurantId: string;
    selectedTab: string;
    tabPosition: number;
}

export const clickSplitMethodTab = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { orderId, amountPaidByUser, numberOfGuest, restaurantId, selectedTab, tabPosition }: SplitMethodTabClickParams,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT_SUCCESS,
        action: DO_CLICK_ACTIONS.SPLIT_METHOD_TAB_CLICK,
        label: selectedTab,
        value: String(tabPosition),
        context: JSON.stringify({
            orderId,
            amountPaidByUser,
            numberOfGuest,
            restaurantId,
        }),
    });
};

interface SplitConfirmCtaParams {
    orderId: string;
    amountPaidByUser: number;
    splitMethod: string;
    numberOfGuest: number;
    eachPersonShare: Record<string, number>;
    restaurantId: string;
}

export const clickSplitConfirmCta = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { orderId, amountPaidByUser, splitMethod, numberOfGuest, eachPersonShare, restaurantId }: SplitConfirmCtaParams,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT_SUCCESS,
        action: DO_CLICK_ACTIONS.SPLIT_CONFIRM_CTA,
        context: JSON.stringify({
            orderId,
            amountPaidByUser,
            splitMethod,
            numberOfGuest,
            eachPersonShare,
            restaurantId,
        }),
    });
};

interface SplitEquallyParams {
    orderId: string;
    amountPaidByUser: number;
    numberOfGuest: number;
    splitMethod: string;
    restaurantId: string;
}

export const clickSplitEqually = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { orderId, amountPaidByUser, numberOfGuest, splitMethod, restaurantId }: SplitEquallyParams,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT_SUCCESS,
        action: DO_CLICK_ACTIONS.SPLIT_EQUALLY,
        context: JSON.stringify({
            orderId,
            amountPaidByUser,
            numberOfGuest,
            splitMethod,
            restaurantId,
        }),
    });
};

interface SplitErrorImpressionParams {
    bookingId: string;
    totalBillAmount: number;
    splitMethod: string;
    error: string;
    restaurantId: string;
}

export const impressionSplitError = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { bookingId, totalBillAmount, splitMethod, error, restaurantId }: SplitErrorImpressionParams,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT_SUCCESS,
        action: DO_IMPRESSION_ACTIONS.SPLIT_ERROR,
        context: JSON.stringify({
            bookingId,
            totalBillAmount,
            splitMethod,
            error,
            restaurantId,
        }),
    });
};

interface SplitUpiEntryParams {
    bookingId: string;
    totalBillAmount: number;
    splitMethod: string;
    upiSelected: number;
    restaurantId: string;
}

export const clickSplitUpiEntry = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { bookingId, totalBillAmount, splitMethod, upiSelected, restaurantId }: SplitUpiEntryParams,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT_SUCCESS,
        action: DO_CLICK_ACTIONS.SPLIT_UPI_ENTRY,
        context: JSON.stringify({
            bookingId,
            totalBillAmount,
            splitMethod,
            upiSelected,
            restaurantId,
        }),
    });
};

interface SplitSuccessParams {
    bookingId: string;
    totalBillAmount: number;
    splitMethod: string;
    restaurantId: string;
}

export const impressionSplitSuccessWidget = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { bookingId, totalBillAmount, splitMethod, restaurantId }: SplitSuccessParams,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT_SUCCESS,
        action: DO_IMPRESSION_ACTIONS.SPLIT_SUCCESS_WIDGET,
        context: JSON.stringify({
            bookingId,
            totalBillAmount,
            splitMethod,
            restaurantId,
        }),
    });
};

export const clickSplitDoneCta = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { bookingId, totalBillAmount, splitMethod, restaurantId }: SplitSuccessParams,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT_SUCCESS,
        action: DO_CLICK_ACTIONS.SPLIT_DONE_CTA,
        context: JSON.stringify({
            bookingId,
            totalBillAmount,
            splitMethod,
            restaurantId,
        }),
    });
};

interface SplitUpiHandleImpressionParams {
    suggestedUpiHandles: string[];
}

export const impressionSplitUpiHandle = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { suggestedUpiHandles }: SplitUpiHandleImpressionParams,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT_SUCCESS,
        action: DO_IMPRESSION_ACTIONS.SPLIT_UPI_HANDLE,
        label: SPLIT_BILL_LABELS.UPI_HANDLE,
        context: JSON.stringify({
            [SPLIT_BILL_CONTEXT_KEYS.SUGGESTED_UPI_HANDLES]: suggestedUpiHandles,
        }),
    });
};

interface SplitUpiHandleClickParams {
    selectedUpiHandle: string;
    suggestedUpiHandles: string[];
    position: number;
}

export const clickSplitUpiHandle = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { selectedUpiHandle, suggestedUpiHandles, position }: SplitUpiHandleClickParams,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT_SUCCESS,
        action: DO_CLICK_ACTIONS.SPLIT_UPI_ENTRY,
        label: SPLIT_BILL_LABELS.UPI_HANDLE,
        value: String(position),
        context: JSON.stringify({
            [SPLIT_BILL_CONTEXT_KEYS.SELECTED_UPI_HANDLE]: selectedUpiHandle,
            [SPLIT_BILL_CONTEXT_KEYS.SUGGESTED_UPI_HANDLES]: suggestedUpiHandles,
        }),
    });
};

interface AddMoreGuestParams {
    orderId: string;
    amountPaidByUser: number;
    numberOfGuest: number;
    splitMethod: string;
    restaurantId: string;
}

export const clickAddMoreGuest = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { orderId, amountPaidByUser, numberOfGuest, splitMethod, restaurantId }: AddMoreGuestParams,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT_SUCCESS,
        action: DO_CLICK_ACTIONS.SPLIT_ADD_MORE_GUEST,
        label: "-",
        value: String(numberOfGuest),
        context: JSON.stringify({
            orderId,
            amountPaidByUser,
            numberOfGuest,
            splitMethod,
            restaurantId,
        }),
    });
};

interface SplitMethodDineCashParams {
    orderId: string;
    amountPaidByUser: number;
    numberOfGuest: number;
    restaurantId: string;
    dinecash: number;
    toggleStatus: boolean;
}

export const impressionSplitMethodDineCash = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    { orderId, amountPaidByUser, numberOfGuest, restaurantId, dinecash, toggleStatus }: SplitMethodDineCashParams,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT_SUCCESS,
        action: DO_IMPRESSION_ACTIONS.SPLIT_METHOD_DINECASH,
        label: String(toggleStatus),
        context: JSON.stringify({
            orderId,
            amountPaidByUser,
            numberOfGuest,
            restaurantId,
            earnedDinecash: dinecash,
        }),
    });
};

export const clickSplitMethodDineCash = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    { orderId, amountPaidByUser, numberOfGuest, restaurantId, dinecash, toggleStatus }: SplitMethodDineCashParams,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT_SUCCESS,
        action: DO_CLICK_ACTIONS.SPLIT_METHOD_DINECASH_CLICK,
        label: String(toggleStatus),
        context: JSON.stringify({
            orderId,
            amountPaidByUser,
            numberOfGuest,
            restaurantId,
            earnedDinecash: dinecash,
        }),
    });
};
